import React, { useEffect, useState, useRef, useCallback } from 'react';
import AWS from 'aws-sdk';
import '../bot.css';
import { handleAmazonConnectChatTransfer } from '../ContactCenter/connect';
import TypingIndicator from '../TypingIndicator/Main';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhoneSlash, FaDesktop, FaRegWindowMaximize } from 'react-icons/fa';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration
} from 'amazon-chime-sdk-js';
import { useAuth } from '../../../routes/AuthContext';

class ChatSession {
  constructor({ contactId, participantToken, sendMessageUrl, socket, onAgentTyping }) {
    this.contactId = contactId;
    this.participantToken = participantToken;
    this.sendMessageUrl = sendMessageUrl;
    this.socket = socket;
    this.onAgentTyping = onAgentTyping;
    this.messageListeners = [];
    this.keepAliveInterval = null;

    this.startKeepAlive();
  }

  startKeepAlive() {
    if (this.socket) {
      this.keepAliveInterval = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              topic: 'aws/heartbeat',
              content: '',
            })
          );
          console.log('[ChatSession] Sent keep-alive heartbeat.');
        }
      }, 30000); // Send every 30 seconds
    }
  }

  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  async sendMessage(content) {
    console.log('[ChatSession] Sending message:', content);
    try {
      const response = await fetch(this.sendMessageUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participantToken: this.participantToken,
          content,
          contentType: 'text/plain',
        }),
      });

      if (!response.ok) {
        throw new Error(`[ChatSession] Failed to send message with status ${response.status}`);
      }

      console.log('[ChatSession] Message sent successfully.');
    } catch (error) {
      console.error('[ChatSession] Error sending message:', error);
    }
  }

  close() {
    this.stopKeepAlive();
    if (this.socket) {
      console.log('[ChatSession] Closing WebSocket connection.');
      this.socket.close();
      this.socket = null; // Clear the reference to ensure proper cleanup
    } else {
      console.log('[ChatSession] No active WebSocket to close.');
    }
  }
}

const AWSLexBot = ({ config }) => {
  const [messages, setMessages] = useState(() => {
    const savedMessages = JSON.parse(localStorage.getItem('lexBotMessages')) || [];
    // if (savedMessages.length === 0 && config.initialMsg) { // Commenting out initial bot message due to new configuration.
    //   return [{ type: 'bot', text: config.initialMsg, source: 'lex' }];
    // }
    // Ensure any initial messages already loaded are assigned a source if missing
    return savedMessages.map((msg) => (msg.type === 'bot' && !msg.source ? { ...msg, source: 'lex' } : msg));
  });  
  const [input, setInput] = useState('');
  const [lexruntimev2, setLexRuntimeV2] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(() => {
    return JSON.parse(localStorage.getItem('lexBotIsChatOpen')) || false;
  });  
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isTransferredToConnect, setIsTransferredToConnect] = useState(false);
  const [isPhoneWidgetVisible, setIsPhoneWidgetVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [localVideoElement, setLocalVideoElement] = useState(null);
  const [remoteVideoElements, setRemoteVideoElements] = useState([null, null]);
  const [contentShareElement, setContentShareElement] = useState(null);
  const [isMeetingMode, setIsMeetingMode] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRemoteVideoOn, setisRemoteVideoOn] = useState(false);
  const [isLocalVideoOn, setisLocalVideoOn] = useState(false);
  const [showDtmf, setShowDtmf] = useState(false);
  const connectSessionRef = useRef(null);
  const lexSessionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const customerMessageCache = useRef(new Set());
  const meetingSessionRef = useRef(null);
  const audioOutputRef = useRef(null);
  // const availableCameraTiles = useRef(["local-video", "remote-video-1"]);
  // const availableContentTiles = useRef(["remote-video-2", "content-share"]);
  const boundTileIds = useRef(new Set()); 
  let mainTileBound = null;
  const { authUser } = useAuth();

  const DTMF_FREQUENCIES = {
    '1': [697, 1209],
    '2': [697, 1336],
    '3': [697, 1477],
    '4': [770, 1209],
    '5': [770, 1336],
    '6': [770, 1477],
    '7': [852, 1209],
    '8': [852, 1336],
    '9': [852, 1477],
    '*': [941, 1209],
    '0': [941, 1336],
    '#': [941, 1477],
  };
  
  const localVideoRef = useCallback((node) => {
    if (node) {
      setLocalVideoElement(node);
    }
  }, []);

  const remoteVideoRefs = [
    useCallback((node) => {
      if (node) {
        setRemoteVideoElements((prev) => {
          const updated = [...prev];
          updated[0] = node;
          return updated;
        });
      }
    }, []),
    useCallback((node) => {
      if (node) {
        setRemoteVideoElements((prev) => {
          const updated = [...prev];
          updated[1] = node;
          return updated;
        });
      }
    }, []),
  ];

  const contentShareRef = useCallback((node) => {
    if (node) {
      setContentShareElement(node);
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAgentTyping]);

  const initializeLexBot = async () => {
    console.log('[AWSLexBot] Initializing Lex bot...');
    const { botAliasId, botId, region, identityPoolId } = config;

    if (!botAliasId || !botId || !region || !identityPoolId) {
      console.error('[AWSLexBot] AWS Lex configuration is incomplete.');
      return;
    }

    AWS.config.region = region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId,
    });

    try {
      await AWS.config.credentials.getPromise();
      console.log('[AWSLexBot] AWS Credentials successfully obtained.');

      const lexClient = new AWS.LexRuntimeV2();
      setLexRuntimeV2(lexClient);

      console.log(`[AWSLexBot] AWS Lex bot initialized: ${botId} (${botAliasId})`);
    } catch (error) {
      console.error('[AWSLexBot] Failed to authenticate AWS Cognito Identity:', error);
    }
  };

  useEffect(() => {
    initializeLexBot().then(() => {
      if (messages.length === 0) {
        const defaultMsg = config.initialMsg;
        const authMsg = config.authInitialMsg;

        const initialMsgToSend =
          authUser?.idToken?.payload?.email ? authMsg : defaultMsg;
        console.log("InitialMsgToSend: ",initialMsgToSend);

        if (initialMsgToSend) {
          sendMessage(initialMsgToSend);
        }
      }
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('lexBotMessages', JSON.stringify(messages));
  }, [messages]);  

  useEffect(() => {
    if (!isTransferredToConnect) {
      localStorage.setItem('lexBotMessages', JSON.stringify(messages));
    }
  }, [messages, isTransferredToConnect]);  

  useEffect(() => {
    const handlePageUnload = () => {
        handleDisconnect(); // Ensure session is properly closed on refresh
    };

    window.addEventListener('beforeunload', handlePageUnload);
    
    return () => {
        window.removeEventListener('beforeunload', handlePageUnload);
    };
  }, []);


  const sendMessage = async (overrideMessage = null) => {
    if (!lexruntimev2) {
        console.warn('[AWSLexBot] Lex Runtime is not initialized.');
        return;
    }

    const messageText = overrideMessage || input.trim();
    const uniqueMessageKey = `<span class="math-inline">\{messageText\}\-</span>{Date.now()}`; // Make it unique per timestamp
    customerMessageCache.current.add(uniqueMessageKey);

    setInput(''); // Clear input field immediately

    // Immediately add the message to the UI with a "sending" indicator
    setMessages((prevMessages) => [
      ...prevMessages,
      { type: 'user', text: messageText, sending: true },
    ]);

    if (isTransferredToConnect && connectSessionRef.current) {
      console.log('[AWSLexBot] Sending message to Amazon Connect...');

      try {
        await connectSessionRef.current.sendMessage(messageText);
      } catch (error) {
        console.error('[AWSLexBot] Failed to send message to Connect:', error);
      }

      // Remove "sending" state once done
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.text === messageText ? { ...msg, sending: false } : msg
        )
      );

      return;
    }

    if (!lexruntimev2) {
      console.warn('[AWSLexBot] Lex Runtime is not initialized.');
      return;
    }

    const { botAliasId, botId, localeId, triggerIntent, contactCenter, transferMessages } = config;

    if (!lexSessionRef.current) {
      lexSessionRef.current = `session-${Date.now()}`;
    }

    // Build session attributes from authUser if available
    const sessionAttributes = {};

    if (authUser?.idToken?.payload) {
      const payload = authUser.idToken.payload;
      sessionAttributes['email'] = payload.email;
      sessionAttributes['CustomerPhone'] = payload.phone_number;
      sessionAttributes['sub'] = payload.sub;
      sessionAttributes['username'] = payload['cognito:username'];
      sessionAttributes['given_name'] = payload.given_name;
      sessionAttributes['family_name'] = payload.family_name;
      sessionAttributes['Authenticated'] = 'true';
      sessionAttributes['CognitoAuthenticated'] = 'true';
      // Include SMS opt-in attribute
      if (payload['custom:sms_optin']) {
        sessionAttributes['sms_optin'] = payload['custom:sms_optin'];
      }
    }

    const params = {
      botAliasId,
      botId,
      localeId,
      sessionId: lexSessionRef.current,
      text: messageText,
      sessionState: {
        sessionAttributes
      }
    };

    try {
      console.log('[AWSLexBot] Sending message to Lex bot...');
      const response = await lexruntimev2.recognizeText(params).promise();
      var Transfer = response.sessionState?.sessionAttributes?.Transfer;
      console.log('Transfer: ',Transfer);
      var Channel = response.sessionState?.intent?.slots?.Channel?.value?.interpretedValue || "Chat";
      console.log('Channel from Lex:', Channel);
      if (Transfer === 'true' && contactCenter?.platform === 'aws-connect') {
        Transfer = 'false'
        if (config.transferringMessage?.trim()) {
          setMessages((prevMessages) => [
            ...prevMessages,
            { type: 'bot', text: config.transferringMessage, source: 'lex' },
          ]);
        }

        // Extract slots and pass them as attributes
        const slots = response.sessionState?.intent?.slots || {};
        const qnaSessionAttributes = response.sessionState?.sessionAttributes || {};
        const contactAttributes = {};

        // Collect slot values into attributes
        Object.keys(slots).forEach((slotKey) => {
          const slotValue = slots[slotKey]?.value?.interpretedValue;
          if (slotValue) {
            contactAttributes[slotKey] = slotValue;
          }
        });

        // Collect session attributes into attributes
        Object.keys(qnaSessionAttributes).forEach((attributeKey) => {
          const attributeValue = qnaSessionAttributes[attributeKey];
          if (attributeValue) {
            contactAttributes[attributeKey] = attributeValue;
          }
        });

        // Add Cognito user info if available
        if (authUser?.idToken?.payload) {
          const payload = authUser.idToken.payload;
          contactAttributes['email'] = payload.email;
          contactAttributes['sub'] = payload.sub;
          contactAttributes['CustomerPhone'] = payload.phone_number;
          contactAttributes['username'] = payload['cognito:username'];
          contactAttributes['Authenticated'] = 'true';
          contactAttributes['CognitoAuthenticated'] = 'true';
          // Include SMS opt-in attribute
          if (payload['custom:sms_optin']) {
            contactAttributes['sms_optin'] = payload['custom:sms_optin'];
          }
        }

        var connectResponse;
        if (Channel === 'Voice') {
          console.log('[AWSLexBot] Channel is Voice, initiating WebRTC call.');
          connectResponse = await initiateWebRTCCall(contactCenter, 'Customer', contactAttributes, response.sessionState?.sessionAttributes?.Flow);
        }
        else if (Channel === 'Chat') {
          connectResponse = await handleAmazonConnectChatTransfer(contactCenter, 'Customer', contactAttributes, response.sessionState?.sessionAttributes?.Flow);
          if (connectResponse) {
            initializeConnectChat(connectResponse);
            setIsTransferredToConnect(true);
            
            if (transferMessages?.transferredMessage?.trim()) {
              setMessages((prevMessages) => [
                ...prevMessages,
                { type: 'bot', text: transferMessages.transferredMessage, source: 'lex' },
              ]);
            }
          }
        }
        return;
      }

      const botResponses = response.messages || [];
      let finalBotMessages = [];
      let llmAnswer = null;
      let sources = null;

      // Decode HTML Entities
      const decodeHTMLEntities = (text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        return doc.documentElement.textContent;
      };

      // Convert markdown links `[text](url)` → `<a href="url">text</a>`
      const convertMarkdownLinksToHTML = (text) => {
        return text.replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
      };

      // Check for LLM Answer and extract content and sources
      const llmResponseMessage = botResponses.find(
        (msg) => msg.contentType === 'PlainText' && msg.content.startsWith('LLM Answer:')
      );

      if (llmResponseMessage) {
        llmAnswer = decodeHTMLEntities(
          llmResponseMessage.content.replace('LLM Answer:\n\n', '').trim().replace(/^"(.*)"$/, '$1')
        );
        const appContext = response.sessionState?.sessionAttributes?.appContext;
        if (appContext) {
          try {
            const parsedContext = JSON.parse(appContext);
            if (parsedContext.altMessages && parsedContext.altMessages.markdown) {
              const sourcesIndex = parsedContext.altMessages.markdown.indexOf('Sources:');
              if (sourcesIndex !== -1) {
                sources = decodeHTMLEntities(
                  parsedContext.altMessages.markdown
                    .substring(sourcesIndex + 'Sources:'.length)
                    .trim()
                )
                .split(',')
                .map((source) => source.trim())
                .join(', ');
              }
            }
          } catch (error) {
            console.error('[AWSLexBot] Failed to parse appContext for sources:', error);
          }
        }
        if (llmAnswer) {
          finalBotMessages.push({
            type: 'bot',
            text: llmAnswer + (sources ? `\n\nSources: ${convertMarkdownLinksToHTML(sources)}` : ''),
            source: 'lex',
            isRichText: true,
          });
        }
      } else {
        // Process regular text and image responses
        botResponses.forEach((msg) => {
          if (msg.contentType === 'PlainText' && !msg.content.startsWith('LLM Answer:')) {
            finalBotMessages.push({
              type: 'bot',
              text: decodeHTMLEntities(msg.content),
              source: 'lex',
              isRichText: true,
            });
          } else if (msg.contentType === 'ImageResponseCard' && msg.imageResponseCard.buttons) {
            finalBotMessages.push({
              type: 'bot',
              text: '',
              buttons: msg.imageResponseCard.buttons.map((btn) => ({
                text: btn.text,
                value: btn.value,
              })),
              source: 'lex',
            });
          }
        });
      }

      // Add new messages to the state and remove the sending status
      if (finalBotMessages.length > 0) {
        setMessages((prevMessages) => [
          ...prevMessages.map((msg) =>
            msg.text === messageText ? { ...msg, sending: false } : msg
          ),
          ...finalBotMessages,
        ]);
      } else {
        // If no new bot messages, just remove the sending status
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.text === messageText ? { ...msg, sending: false } : msg
          )
        );
      }
    } catch (error) {
      console.error('[AWSLexBot] Error communicating with AWS Lex:', error);

      // Mark message as failed if an error occurs
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.text === messageText ? { ...msg, sending: 'error' } : msg
        )
      );
    }
  };  

  const initiateWebRTCCall = async (contactCenterConfig, customerName = "Customer", additionalAttributes = {}, flowId_param = null) => {
    console.log('[AWSLexBot] Initiating WebRTC call...');
    const {
      instanceId,
      apiGatewayEndpoint,
      contactAttributes,
    } = contactCenterConfig;
  
    var {
      flowId,
    } = contactCenterConfig
  
    if (flowId_param != null) {
      flowId = flowId_param;
    };
  
    if (!apiGatewayEndpoint?.startWebRTCContact) {
      console.error('[AWSLexBot] startWebRTCContact API endpoint is not configured.');
      setMessages((prevMessages) => [
        ...prevMessages,
        { type: 'bot', text: 'Error: WebRTC call setup failed - missing API endpoint.', source: 'lex' },
      ]);
      return;
    }
  
    try {
      const mergedAttributes = {
        ...(contactAttributes || {}),
        ...additionalAttributes,
      };
  
      const response = await fetch(apiGatewayEndpoint.startWebRTCContact, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instanceId,
          flowId,
          customerName: customerName || 'Customer',
          contactAttributes: mergedAttributes, // Use merged attributes
        }),
      });
  
      if (!response.ok) {
        const errorBody = await response.text();
        console.error('[AWSLexBot] Failed to start WebRTC contact:', response.status, errorBody);
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'bot', text: `Error starting call: ${response.status} - ${errorBody}`, source: 'lex' },
        ]);
        return;
      }
  
      const webrtcContactInfo = await response.json();
      console.log('[AWSLexBot] WebRTC Contact Info:', webrtcContactInfo);

      const { ParticipantToken, ContactId } = webrtcContactInfo.ConnectionData;

      const createParticipantResponse = await fetch(contactCenterConfig.apiGatewayEndpoint.createParticipantConnection, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participantToken: webrtcContactInfo.ParticipantToken,
          type: ['CONNECTION_CREDENTIALS'],
        }),
      });

      if (!createParticipantResponse.ok) {
        console.error('[AWSLexBot] Failed to create participant connection:', await createParticipantResponse.text());
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'bot', text: 'Failed to initialize participant connection.', source: 'lex' },
        ]);
        return;
      }

      const { ConnectionCredentials } = await createParticipantResponse.json();
      connectSessionRef.current = {
        contactId: ContactId,
        connectionToken: ConnectionCredentials.ConnectionToken,
        sendMessageUrl: contactCenterConfig.apiGatewayEndpoint.sendMessage
      };
  
      // --- CHIME SDK INITIALIZATION ---
      const meetingConfiguration = new MeetingSessionConfiguration(
        webrtcContactInfo.ConnectionData.Meeting,
        webrtcContactInfo.ConnectionData.Attendee
      );

      const logger = new ConsoleLogger('ChimeSDK', LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);

      // --- DEVICE CONFIGURATION (Label Trigger and Initial Device Setup) ---
      const deviceLabelTrigger = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          stream.getTracks().forEach(track => track.stop());
          console.log('[AWSLexBot] Microphone and camera permissions granted via label trigger.');
          return new MediaStream(); // Return an empty stream as per documentation for triggering labels
        } catch (error) {
          console.error('[AWSLexBot] Microphone or camera permission denied via label trigger:', error);
          setMessages((prevMessages) => [
            ...prevMessages,
            { type: 'warning', text: 'Microphone and camera permissions are recommended for the best experience.', source: 'lex' },
          ]);
          return new MediaStream(); // Still return an empty stream to proceed (no labels might be available)
        }
      };

      // Set the device label trigger BEFORE creating the meeting session
      deviceController.setDeviceLabelTrigger(deviceLabelTrigger);

      // Create the meeting session AFTER the label trigger is set
      meetingSessionRef.current = new DefaultMeetingSession(meetingConfiguration, logger, deviceController);

      // --- INITIAL AUDIO INPUT DEVICE CONFIGURATION ---
      const audioInputDevices = await meetingSessionRef.current.audioVideo.listAudioInputDevices();
      if (audioInputDevices.length > 0) {
        // Assuming you want to select the first available audio input device by default
        await meetingSessionRef.current.audioVideo.startAudioInput(audioInputDevices[0]);
        console.log('[AWSLexBot] Audio input started with device:', audioInputDevices[0].label);
      } else {
        console.warn('[AWSLexBot] No audio input devices found after label trigger.');
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'warning', text: 'No audio input devices found.', source: 'lex' },
        ]);
      }

      // --- INITIAL AUDIO OUTPUT DEVICE CONFIGURATION ---
      const audioOutputDevices = await meetingSessionRef.current.audioVideo.listAudioOutputDevices();
      if (audioOutputDevices.length > 0) {
        // Assuming you want to select the first available audio output device by default
        await meetingSessionRef.current.audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
        console.log('[AWSLexBot] Audio output chosen with device:', audioOutputDevices[0].label);
      } else {
        console.warn('[AWSLexBot] No audio output devices found.');
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'warning', text: 'No audio output devices found.', source: 'lex' },
        ]);
      }

      // --- BIND AUDIO OUTPUT TO AUDIO ELEMENT ---
      if (audioOutputRef.current) {
        meetingSessionRef.current.audioVideo.bindAudioElement(audioOutputRef.current);
        console.log('[AWSLexBot] Audio output bound to audio element.');
      } else {
        console.warn('[AWSLexBot] Audio output ref is not yet available.');
      }

      // --- INITIAL VIDEO DEVICE CONFIGURATION (Optional - You might handle this later based on user interaction) ---
      const videoInputDevices = await meetingSessionRef.current.audioVideo.listVideoInputDevices();
      if (videoInputDevices.length > 0) {
        console.log('[AWSLexBot] Video input devices found:', videoInputDevices.map(device => device.label));
      } else {
        console.warn('[AWSLexBot] No video input devices found after label trigger.');
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'warning', text: 'No video input devices found.', source: 'lex' },
        ]);
      }

      // --- AUDIO VIDEO OBSERVER ---
      const audioVideoObserver = {
        audioVideoDidStart: () => {
          console.log('[AWSLexBot] Audio Video session started.');
        },
        audioVideoDidStartConnecting: () => {
          console.log('[AWSLexBot] Audio Video session connecting.');
        },
        audioVideoDidStop: (sessionStatus) => {
          console.log('[AWSLexBot] Audio Video session stopped:', sessionStatus);
          handleDisconnect();
        },
        videoTileDidUpdate: (tileState) => {
          console.log('[AWSLexBot] Video tile updated:', tileState);
        
          if (!meetingSessionRef.current || !meetingSessionRef.current.audioVideo) return;
        
          const { tileId, isContent, localTile, boundVideoStream } = tileState;
        
          if (!boundVideoStream || !boundVideoStream.active) {
            console.log('[AWSLexBot] Stream not active yet for tile:', tileId);
            return;
          }

          // Hide the DTMF keypad and toggle button when video starts
          setShowDtmf(false);
          const dtmfToggleBtn = document.querySelector('.dtmf-toggle-button');
          if (dtmfToggleBtn) {
            dtmfToggleBtn.style.display = 'none';
          }
        
          const audioVideo = meetingSessionRef.current.audioVideo;
        
          const mainSlots = {
            'content-share': document.getElementById('content-share-main'),
            'remote-video-1': document.getElementById('remote-video-1-main'),
            'local-video': document.getElementById('local-video-main'),
          };
        
          const sideSlots = {
            'remote-video-1': document.getElementById('remote-video-1-side'),
            'local-video': document.getElementById('local-video-side'),
          };
        
          const priorityOrder = ['content-share', 'remote-video-1', 'local-video'];
        
          const getType = (s) => s.isContent ? 'content-share' : s.localTile ? 'local-video' : 'remote-video-1';
        
          // Gather all active tiles (including this one)
          const activeTiles = new Set(boundTileIds.current);
          activeTiles.add(tileId);
        
          // Determine the highest-priority active type
          let topPriorityType = getType(tileState);
          activeTiles.forEach(id => {
            const s = audioVideo.getVideoTile(id)?.state();
            if (s) {
              const type = getType(s);
              if (priorityOrder.indexOf(type) < priorityOrder.indexOf(topPriorityType)) {
                topPriorityType = type;
              }
            }
          });
        
          // For each active tile, assign proper slot
          activeTiles.forEach(id => {
            const s = audioVideo.getVideoTile(id)?.state();
            if (!s || !s.boundVideoStream || !s.boundVideoStream.active) return;
        
            const type = getType(s);
            const shouldBeMain = (type === topPriorityType);
            const targetSlot = shouldBeMain ? mainSlots[type] : sideSlots[type];
        
            if (!targetSlot) {
              console.warn(`[AWSLexBot] No available slot for tile ${id} of type ${type}`);
              return;
            }
        
            // Skip if already correctly bound
            if (targetSlot.dataset.tileId === String(id)) {
              console.log(`[AWSLexBot] Tile ${id} already bound to ${targetSlot.id}, skipping`);
              return;
            }
        
            // Unbind from any slot it was bound to
            const allSlots = [...Object.values(mainSlots), ...Object.values(sideSlots)];
            allSlots.forEach(el => {
              if (el && el.dataset.tileId === String(id)) {
                audioVideo.unbindVideoElement(id);
                delete el.dataset.tileId;
                el.style.display = 'none';
              }
            });
        
            // Bind to target slot
            audioVideo.bindVideoElement(id, targetSlot);
            targetSlot.dataset.tileId = id;
            targetSlot.style.display = 'block';
        
            if (shouldBeMain) {
              mainTileBound = { type, tileId: id };
            }
        
            console.log(`Bound ${type} (tile ${id}) to ${targetSlot.id}`);
          });
        
          // React state updates (based on the incoming tile type)
          const currentTileType = getType(tileState);
          if (currentTileType === 'content-share') {
            setIsScreenSharing(true);
          } else if (currentTileType === 'remote-video-1') {
            setisRemoteVideoOn(true);
          } else if (currentTileType === 'local-video') {
            setisLocalVideoOn(true);
          }
        
          if (!isMeetingMode) setIsMeetingMode(true);
          boundTileIds.current.add(tileId);
        },             
        videoTileWasRemoved: (tileId) => {
          console.log('[AWSLexBot] Video tile removed:', tileId);
        
          if (!meetingSessionRef.current || !meetingSessionRef.current.audioVideo) return;
        
          try {
            meetingSessionRef.current.audioVideo.unbindVideoElement(tileId);
          } catch (error) {
            console.error('[AWSLexBot] Error unbinding tile:', error);
          }
        
          const elements = [
            document.getElementById('content-share-main'),
            document.getElementById('local-video-main'),
            document.getElementById('local-video-side'),
            document.getElementById('remote-video-1-main'),
            document.getElementById('remote-video-1-side')
          ];
        
          const el = elements.find(e => e?.dataset.tileId === String(tileId));
        
          if (el) {
            console.log('[AWSLexBot] Freed video element:', el.id);
            el.style.display = 'none';
            el.srcObject = null;
            delete el.dataset.tileId;
            if (el.id === 'content-share-main') setIsScreenSharing(false);
          }
        
          boundTileIds.current.delete(tileId);
        },                     
        audioVideoDidBecomePoor: () => {
          console.warn('[AWSLexBot] Connection quality is poor.');
          setMessages((prevMessages) => [
            ...prevMessages,
            { type: 'warning', text: 'Connection quality is poor.', source: 'lex' },
          ]);
        },
        // Add other observer callbacks as needed
      };

      // Register the observer
      meetingSessionRef.current.audioVideo.addObserver(audioVideoObserver);

      // Start audio
      meetingSessionRef.current.audioVideo.start();
  
      setMessages((prevMessages) => [
        { type: 'info', text: 'Voice call connecting...', source: 'lex' },
      ]);
      setIsTransferredToConnect(true);
      setIsPhoneWidgetVisible(true);
  
    } catch (error) {
      console.error('[AWSLexBot] Error initiating WebRTC call:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { type: 'bot', text: `Error starting call: ${error.message}`, source: 'lex' },
      ]);
    }
  };

  const playDTMFTone = (digit) => {
    const freqs = DTMF_FREQUENCIES[digit];
    if (!freqs) return;
  
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  
    const osc1 = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
  
    osc1.frequency.value = freqs[0];
    osc2.frequency.value = freqs[1];
    osc1.type = 'sine';
    osc2.type = 'sine';
  
    gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime); // moderate volume
  
    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(audioCtx.destination);
  
    osc1.start();
    osc2.start();
  
    osc1.stop(audioCtx.currentTime + 0.15); // ~150ms tone
    osc2.stop(audioCtx.currentTime + 0.15);
  
    // Clean up
    osc1.onended = () => audioCtx.close();
  };  

  const initializeConnectChat = async ({ ContactId, ParticipantToken }) => {
    console.log('[AWSLexBot] Initializing Amazon Connect chat...');
    localStorage.removeItem('lexBotMessages'); // Clear stored messages on transfer
    const { contactCenter } = config;
  
    const response = await fetch(contactCenter.apiGatewayEndpoint.createParticipantConnection, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        participantToken: ParticipantToken,
        type: ['WEBSOCKET', 'CONNECTION_CREDENTIALS'],
      }),
    });
  
    if (!response.ok) {
      console.error('[AWSLexBot] Failed to create participant connection:', await response.text());
      return;
    }
  
    const { Websocket, ConnectionCredentials } = await response.json();
    const { Url: websocketUrl } = Websocket || {};
  
    if (!websocketUrl) {
      console.error('[AWSLexBot] WebSocket URL is missing in the response.');
      return;
    }
  
    console.log('[AWSLexBot] Establishing WebSocket connection...');
    const socket = new WebSocket(websocketUrl);
  
    socket.onopen = () => {
      console.log('[AWSLexBot] WebSocket connection established.');
    
      socket.send(
        JSON.stringify({
          topic: 'aws/subscribe',
          content: {
            topics: ['aws/chat'],
          },
        })
      );
      console.log('Subscription message sent to aws/chat topic.');
    };
  
    socket.onmessage = (event) => {
      console.log('[AWSLexBot] Incoming message:', event.data);
      try {
        const data = JSON.parse(event.data);
   
        if (data.contentType === 'application/json' && data.content) {
          const parsedContent = JSON.parse(data.content);
   
          // Handle Agent Connected Event
          if (parsedContent.ContentType === 'application/vnd.amazonaws.connect.event.participant.joined' && parsedContent.ParticipantRole === 'AGENT') {
            const agentName = parsedContent.DisplayName;
            setMessages((prevMessages) => [
             ...prevMessages,
             { type: 'info', text: `Agent ${agentName} connected`, source: 'connect' },
            ]);
           
            // Calculate and set --text-width
            setTimeout(() => {
             const infoElements = document.querySelectorAll('.chat-message.info:last-child');
             if (infoElements.length > 0) {
              const infoElement = infoElements[0];
              const textWidth = infoElement.getBoundingClientRect().width; // Use getBoundingClientRect()
              infoElement.style.setProperty('--text-width', `${textWidth}px`);
             }
            }, 0);
            return;
           }
           

          if (data.contentType === 'application/json' && data.content) {
            const parsedContent = JSON.parse(data.content);
      
            // Handle Agent Typing Event
            if (parsedContent.ContentType === 'application/vnd.amazonaws.connect.event.typing' && parsedContent.ParticipantRole === 'AGENT') {
              setIsAgentTyping(true);
              // Clear typing indicator after a delay (adjust as needed)
              setTimeout(() => {
                setIsAgentTyping(false);
              }, 4500);
              return;
            }
          }

          // Handle Agent Disconnected Event
          if (parsedContent.ContentType === 'application/vnd.amazonaws.connect.event.chat.ended') {
            setMessages((prevMessages) => [
             ...prevMessages,
             { type: 'info', text: `This chat has been disconnected.`, source: 'connect' },
             { type: 'info', text: `Thank you for contacting us.`, source: 'connect' },
            ]);
           
            // Calculate and set --text-width
            setTimeout(() => {
             const infoElements = document.querySelectorAll('.chat-message.info:last-child');
             if (infoElements.length > 0) {
              const infoElement = infoElements[0];
              const textWidth = infoElement.getBoundingClientRect().width; // Use getBoundingClientRect()
              infoElement.style.setProperty('--text-width', `${textWidth}px`);
             }
            }, 0);
            return;
           }
   
          if (parsedContent.ContentType === 'text/plain') {
            const isEchoedMessage =
              parsedContent.ParticipantRole === 'CUSTOMER';
   
            // Ignore echoed customer messages
            if (isEchoedMessage) {
              console.log('[AWSLexBot] Ignoring echoed customer message:', parsedContent.Content);
              return;
            }
   
            // Process valid messages
            if (!customerMessageCache.current.has(parsedContent.Id)) {
              console.log('[AWSLexBot] Handling text/plain message:', parsedContent.Content);
   
              try {
                let messageText = parsedContent.Content;
                let buttons = [];
   
                // Attempt to extract buttons if the message contains a JSON object
                const jsonMatch = messageText.match(/\{.*\}/);
                if (jsonMatch) {
                  try {
                    const parsedJson = JSON.parse(jsonMatch[0]); // Extract JSON
                    if (parsedJson.Buttons && Array.isArray(parsedJson.Buttons)) {
                      buttons = parsedJson.Buttons.map((btn) => ({
                        text: btn,
                        value: btn,
                      }));
                    }
                    messageText = messageText.replace(jsonMatch[0], "").trim().replace(/\s+/g, " "); // Remove JSON & extra spaces
                  } catch (jsonError) {
                    console.warn("[AWSLexBot] Failed to parse embedded JSON buttons:", jsonError);
                  }
                }
   
                setMessages((prevMessages) => [
                  ...prevMessages,
                  {
                    type: parsedContent.ParticipantRole === 'CUSTOMER' ? 'user' : 'agent',
                    text: messageText,
                    id: parsedContent.Id,
                    source: 'connect',
                    buttons: buttons.length > 0 ? buttons : null, // Only include if buttons exist
                  },
                ]);
              } catch (error) {
                console.error("[AWSLexBot] Error processing incoming message:", error);
              }
   
              // Add message content to cache to avoid duplicates
              customerMessageCache.current.add(parsedContent.Id);
            }
          }
        }
      } catch (error) {
        console.error('[AWSLexBot] Error parsing incoming message:', error);
      }
    };
  
    socket.onerror = (error) => {
      console.error('[AWSLexBot] WebSocket error:', error);
    };
  
    socket.onclose = () => {
      console.log('[AWSLexBot] WebSocket connection closed.');
      connectSessionRef.current = null; // Ensure the session reference is cleared
      setIsTransferredToConnect(false); // Reset state for Connect transfer
    };
  
    connectSessionRef.current = new ChatSession({
      contactId: ContactId,
      participantToken: ConnectionCredentials.ConnectionToken,
      sendMessageUrl: contactCenter.apiGatewayEndpoint.sendMessage,
      socket,
      onAgentTyping: setIsAgentTyping,
    });
  };  

  const toggleMute = () => {
    if (meetingSessionRef.current) {
      if (meetingSessionRef.current.audioVideo.realtimeIsLocalAudioMuted()) {
        meetingSessionRef.current.audioVideo.realtimeUnmuteLocalAudio();
        setIsMuted(false);
        console.log('[AWSLexBot] Unmuted local audio.');
        // You might want to update your UI to reflect the unmuted state
      } else {
        meetingSessionRef.current.audioVideo.realtimeMuteLocalAudio();
        setIsMuted(true);
        console.log('[AWSLexBot] Muted local audio.');
        // You might want to update your UI to reflect the muted state
      }
    } else {
      console.warn('[AWSLexBot] Meeting session is not initialized, cannot toggle mute.');
    }
  };

  const toggleScreenShare = async () => {
    if (!meetingSessionRef.current || !meetingSessionRef.current.audioVideo) {
      console.warn('[AWSLexBot] Meeting session is not initialized, cannot toggle screen share.');
      return;
    }
  
    if (!isScreenSharing) {
      try {
        await meetingSessionRef.current.audioVideo.startContentShareFromScreenCapture();
        setIsScreenSharing(true);
        console.log('[AWSLexBot] Screen sharing started.');
      } catch (err) {
        console.error('[AWSLexBot] Failed to start screen share:', err);
      }
    } else {
      meetingSessionRef.current.audioVideo.stopContentShare();
      setIsScreenSharing(false);
      console.log('[AWSLexBot] Screen sharing stopped.');
    }
  };  

  const startLocalVideo = async () => {
    if (meetingSessionRef.current) {
      try {
        const videoInputDevices = await meetingSessionRef.current.audioVideo.listVideoInputDevices();
        if (videoInputDevices.length > 0) {
          if (!isMeetingMode) setIsMeetingMode(true);
          await meetingSessionRef.current.audioVideo.startVideoInput(videoInputDevices[0]); // Or allow user to select
          await meetingSessionRef.current.audioVideo.startLocalVideoTile();
          setIsVideoOn(true);
          console.log('[AWSLexBot] Started local video.');
          // You might want to update your UI to show the local video feed and a "stop video" button
        } else {
          console.warn('[AWSLexBot] No video input devices found to start video.');
          setMessages((prevMessages) => [
            ...prevMessages,
            { type: 'warning', text: 'No video input devices found.', source: 'lex' },
          ]);
        }
      } catch (error) {
        console.error('[AWSLexBot] Error starting local video:', error);
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: 'error', text: `Error starting local video: ${error.message}`, source: 'lex' },
        ]);
      }
    } else {
      console.warn('[AWSLexBot] Meeting session is not initialized, cannot start video.');
    }
  };

  const stopLocalVideo = () => {
    if (meetingSessionRef.current) {
      meetingSessionRef.current.audioVideo.stopLocalVideoTile();
      meetingSessionRef.current.audioVideo.stopVideoInput();
      setIsVideoOn(false);
      console.log('[AWSLexBot] Stopped local video.');
      // You might want to update your UI to hide the local video feed and show a "start video" button
    } else {
      console.warn('[AWSLexBot] Meeting session is not initialized, cannot stop video.');
    }
  };

  const sendDTMF = async (digit) => {
    playDTMFTone(digit);
  
    const connectionToken = connectSessionRef.current?.connectionToken;
  
    if (!connectionToken) {
      console.warn('[AWSLexBot] Cannot send DTMF — missing connection token.');
      return;
    }
  
    try {
      const res = await fetch('https://participant.connect.us-east-1.amazonaws.com/participant/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Amz-Bearer': connectionToken,
        },
        body: JSON.stringify({
          Content: digit,
          ContentType: 'audio/dtmf',
          ClientToken: crypto.randomUUID(),
        }),
      });
  
      if (!res.ok) {
        console.error('[AWSLexBot] Failed to send DTMF:', await res.text());
      } else {
        console.log('[AWSLexBot] DTMF sent successfully.');
      }
    } catch (err) {
      console.error('[AWSLexBot] Error sending DTMF:', err);
    }
  };  

  const toggleChat = () => {
    if (!isChatOpen) {
      // Check if there are no messages or the first message doesn't have a source
      if (messages.length === 0 || messages.every((msg) => msg.type !== 'user')) {
        const defaultMsg = config.initialMsg;
        const authMsg = config.authInitialMsg;

        const initialMsgToSend =
          authUser?.idToken?.payload?.email ? authMsg : defaultMsg;
        console.log("InitialMsgToSend: ",initialMsgToSend);

        setTimeout(() => {
          sendMessage(initialMsgToSend);
        }, 100);
      }
      if (isTransferredToConnect) {
        connectSessionRef.current?.close();
        setIsTransferredToConnect(false);
      }
    }
    setIsChatOpen((prev) => !prev);
  };
  
  useEffect(() => {
    if (messages.length > 0) {
        localStorage.setItem('lexBotMessages', JSON.stringify(messages));
    }
  }, [messages]); 

  useEffect(() => {
    try {
      localStorage.setItem('lexBotIsChatOpen', JSON.stringify(isChatOpen));
    } catch {}

    try {
      window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: !!isChatOpen, source: 'aws-lex' } }));
    } catch {}
  }, [isChatOpen]);

  const handleDisconnect = async () => {
    lexSessionRef.current = null;
    if (meetingSessionRef.current) {
      console.log('[AWSLexBot] Hanging up WebRTC call.');
      meetingSessionRef.current.audioVideo.stop();
      meetingSessionRef.current = null;
      setIsTransferredToConnect(false); // Reset Connect transfer state
      customerMessageCache.current.clear(); // Reset cache
      localStorage.removeItem('lexBotMessages'); // Clear stored messages
      setMessages([]);
      setIsChatOpen(false);
      setIsPhoneWidgetVisible(false);
      setIsMeetingMode(false);
      setIsMuted(false);
      setIsScreenSharing(false);
      setIsVideoOn(false);
      setShowDtmf(false);
      const dtmfToggleBtn = document.querySelector('.dtmf-toggle-button');
      if (dtmfToggleBtn) {
        dtmfToggleBtn.style.display = 'block';
      }
      return;
    }

    if (connectSessionRef.current && typeof connectSessionRef.current.close === 'function') {
      const { contactCenter } = config;
      const contactId = connectSessionRef.current.contactId;
      const instanceId = contactCenter.instanceId;
  
      try {
        console.log('[AWSLexBot] Invoking StopContact API...');
        const response = await fetch(contactCenter.apiGatewayEndpoint.stopContact, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ContactId: contactId,
            InstanceId: instanceId,
          }),
        });
  
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[AWSLexBot] Failed to stop contact. Status: ${response.status}, Response: ${errorText}`);
        } else {
          console.log('[AWSLexBot] StopContact API successfully invoked.');
        }
      } catch (error) {
        console.error('[AWSLexBot] Error invoking StopContact API:', error);
      }
  
      // Close the session after invoking the API
      connectSessionRef.current.close(); // Explicitly close the session
      connectSessionRef.current = null;  // Clear the reference
    }
  
    customerMessageCache.current.clear(); // Reset cache
    localStorage.removeItem('lexBotMessages'); // Clear stored messages
    setIsTransferredToConnect(false);
    setMessages([]);
    setIsChatOpen(false);
  };  

  const handleCloseClick = () => {
    if (isTransferredToConnect) {
      setShowDisconnectConfirm(true); // Show confirmation only for Connect
    } else {
      handleDisconnect(); // Directly disconnect if not connected to Amazon Connect
    }
  };  

  const confirmDisconnect = () => {
    setShowDisconnectConfirm(false);
    handleDisconnect();
  };

  const cancelDisconnect = () => setShowDisconnectConfirm(false);

  return (
    <>
      <div className="chat-instruction">{config.infoText || 'Start chatting with our bot!'}</div>
      <button
        onClick={toggleChat}
        className="chat-open-button"
        aria-label="Open Chat"
        title="Open Chat"
      >
        <img
          src={require('../../../assets/image/chat icon.png')}
          alt="Chat Icon"
          style={{
            width: '24px',
            height: '24px',
            filter: 'brightness(0) invert(1)'
          }}
        />
      </button>

      {isChatOpen && (
        <div className={`aws-lex-bot-container ${isMeetingMode ? 'fullscreen' : ''}`}>
          <div className="aws-lex-bot-header">
            {config.closeText || 'Close'}
            <button
              className="close-chat-button"
              onClick={handleCloseClick}
              style={{
                backgroundColor: '#003366',
                color: '#ffffff',
                border: 'none',
                position: 'absolute',
                right: '10px',
                top: '10px',
              }}
            >
              ✖
            </button>
          </div>

          <div className="aws-lex-bot-messages">
            {!isPhoneWidgetVisible && messages.map((msg, index) => (
              <div key={`${msg.text}-${index}`} className={`chat-message ${msg.type}`}>
                {msg.type === 'bot' && msg.source === 'lex' && msg.text && (
                  <div className="chat-bot-icon">🤖</div>
                )}

                {msg.text && (
                  msg.isRichText ? (
                    <div className={`chat-bubble ${msg.type}`} dangerouslySetInnerHTML={{ __html: String(msg.text) }} />
                  ) : (
                    <div className={`chat-bubble ${msg.type}`}>
                      {String(msg.text)}
                    </div>
                  )
                )}

                {msg.buttons && (
                  <div className={msg.source === 'lex' ? 'chat-buttons' : 'agent-buttons'}>
                    {msg.buttons.map((btn, btnIndex) => (
                      <button
                        key={btnIndex}
                        className={`chat-button ${msg.clicked ? 'clicked' : ''}`}
                        onClick={(e) => {
                          if (!msg.clicked) {
                            sendMessage(btn.value);
                            msg.clicked = true;
                            e.target.classList.add('clicked');
                          }
                        }}
                      >
                        {btn.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {!isPhoneWidgetVisible && isAgentTyping && (
              <div className="chat-message bot">
                <div className="chat-bubble agent-typing">
                  <TypingIndicator />
                </div>
              </div>
            )}

            {!isPhoneWidgetVisible && <div ref={messagesEndRef} />}

            {isPhoneWidgetVisible && (
              <div className={`meeting-layout ${isMeetingMode ? 'active' : ''}`}>
                <div className="phone-widget-container">
                <div className="video-tiles-container">
                  <div className="video-main-tile">
                    <video id="content-share-main" className="video-tile content-share" autoPlay playsInline />
                    <video id="local-video-main" className="video-tile local-video" autoPlay muted playsInline />
                    <video id="remote-video-1-main" className="video-tile remote-video-1" autoPlay playsInline />
                  </div>

                  <div className="video-side-tiles">
                    <video id="remote-video-1-side" className="video-tile remote-video-1 small" autoPlay playsInline />
                    <video id="local-video-side" className="video-tile local-video small" autoPlay muted playsInline />
                  </div>
                </div>
                  <div className="phone-controls">
                    <div className="control-row">
                      <button className={`phone-button small mute-button ${isMuted ? 'muted' : ''}`} onClick={toggleMute}>
                        {isMuted ? <FaMicrophoneSlash color="white" /> : <FaMicrophone />}
                      </button>
                      <div style={{ width: '10px' }} />
                      <button className={`phone-button small video-button ${isVideoOn ? 'video-on' : ''}`} onClick={isVideoOn ? stopLocalVideo : startLocalVideo}>
                        {isVideoOn ? <FaVideo /> : <FaVideoSlash />}
                      </button>
                      <div style={{ width: '10px' }} />
                      <button className={`phone-button small screen-share-button ${isScreenSharing ? 'sharing' : ''}`} onClick={toggleScreenShare}>
                        {isScreenSharing ? <FaDesktop /> : <FaRegWindowMaximize />}
                      </button>
                    </div>
                    <div className={`dtmf-pad ${showDtmf ? 'visible' : 'hidden'}`}>
                      {['1','2','3','4','5','6','7','8','9','*','0','#'].map(dtmf => (
                        <button key={dtmf} className={`phone-button small dtmf-button ${dtmf}`} onClick={() => sendDTMF(dtmf)}>
                          {dtmf}
                        </button>
                      ))}
                    </div>

                    <button
                      className="phone-button small dtmf-toggle-button"
                      onClick={() => setShowDtmf(prev => !prev)}
                      aria-label="Toggle Keypad"
                      title="Toggle Keypad"
                    >
                      <div className="dtmf-icon-grid">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <span key={i} className="dot" />
                        ))}
                        <span className="dot zero" />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!isPhoneWidgetVisible && (
            <div className="aws-lex-bot-input">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') sendMessage();
                }}
                placeholder="Type your message..."
              />
              <button onClick={() => sendMessage()} style={{ backgroundColor: '#E75934', color: '#fff' }}>
                Send
              </button>
            </div>
          )}
        </div>
      )}

      {showDisconnectConfirm && (
        <div className="disconnect-confirm-overlay">
          <div className="disconnect-confirm-popup">
            <p>Are you sure you want to {isPhoneWidgetVisible ? 'end the call' : 'disconnect the chat'}?</p>
            <button onClick={confirmDisconnect} style={{ marginRight: '10px' }}>Yes</button>
            <button onClick={cancelDisconnect}>No</button>
          </div>
        </div>
      )}

      <audio ref={audioOutputRef} />
    </>
  );
};

export default AWSLexBot;
